import React, { useState, useEffect, useRef } from 'react';
import './ResignList.css';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';
import { selectStyles } from '../../../assets/Styles/selectStyle';
import { getStates, fetchMonths, getDistrict } from '../../../services/monthlyAttendanceService';
import { getAllEmployesBasedOnStateDistrict, sendResignationMail } from '../../../services/resignationService';
import axios from 'axios';
import { getAuditLog } from '../../../utils/calculateDaysInMonthUtils';
import { getEmployeResignationByEmpId } from '../../../services/roleServiceMapService';
import { capitalizeFirstLetter } from '../../../utils/validations';

const ResignList = () => {
  const navigate = useNavigate();
  const [state, setState] = useState(3);
  const [district, setDistrict] = useState(null);
  const [stateOptions, setStateOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [existingData, setExistingData] = useState(null);
  const effectRan = useRef(false);
  const [file, setFile] = useState(null);
  const [isFocused, setIsFocused] = useState({
    state: false,
    district: false,
    employee: false,
  });
  const [errors, setErrors] = useState({});

  const roleId = localStorage.getItem('roleId');
  const employeeId = localStorage.getItem('employeeId');

  const [formData, setFormData] = useState({
    empId: '',
    empName: '',
    contactNumber: '',
    designation: '',
    reason: '',
    date: '',
    releavingDate: '',
    client: '',
    project: '',
  });

  // Determine if form submission is enabled
  const isFetchDataEnabled = [
    formData.empId,
    formData.empName,
    formData.contactNumber,
    formData.designation,
    formData.client,
    formData.project,
    formData.reason,
    formData.date,
    formData.releavingDate,
    // file,
  ].every(Boolean);
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
      if (!validTypes.includes(selectedFile.type)) {
        alert('Please upload a valid file: PDF, DOC, DOCX, JPG, PNG');
        e.target.value = '';
        setFile(null);
      } else {
        setFile(selectedFile);
      }
    } else {
      setFile(null);
    }
  };
  // Fetch states (only for roleId === 1)
  useEffect(() => {
    if (roleId === '1' || roleId === '15') {
      const fetchStates = async () => {
        try {
          const data = await getStates();
          setStateOptions(
            data.map((item) => ({
              value: item.id,
              label: capitalizeFirstLetter(item.stateName),
            }))
          );
        } catch (error) {
          console.error('Error fetching states:', error);
          setErrors((prev) => ({ ...prev, general: 'Failed to load states' }));
        }
      };
      if (!effectRan.current) {
        fetchStates();
      }
      return () => {
        effectRan.current = true;
      };
    }
  }, [roleId]);

  // Fetch districts when state changes (only for roleId === 1)
  useEffect(() => {
    if (roleId === '1' || roleId === '15'&& state) {
      const fetchDistricts = async () => {
        try {
          const data = await getDistrict(state);
          setDistrictOptions(
            data.map((item) => ({
              value: item.districtId,
              label: item.districtName,
            }))
          );
        } catch (error) {
          console.error('Error fetching districts:', error);
          setErrors((prev) => ({ ...prev, general: 'Failed to load districts' }));
        }
      };
      fetchDistricts();
    } else {
      setDistrictOptions([]);
      setDistrict(null);
      setEmployees([]);
      setEmployeeOptions([]);
      setSelectedEmployee(null);
    }
  }, [state, roleId]);

  // Fetch employees when state and district change (only for roleId === 1)
  useEffect(() => {
    if (roleId === '1' || roleId === '15' && state) {
      const fetchListOfEmployees = async () => {
        try {
          const data = await getAllEmployesBasedOnStateDistrict(state, district);
          const sortedData = data.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
          setEmployees(data);
          setEmployeeOptions(
            sortedData.map((emp) => ({
              value: emp.employeeId,
              label: `${emp.employeeName} (${emp.employeeId})`,
              employeeData: emp,
            }))
          );
        } catch (error) {
          console.error('Error fetching employees:', error);
          setErrors((prev) => ({ ...prev, general: 'Failed to load employees' }));
        }
      };
      fetchListOfEmployees();
    }
  }, [state, district, roleId]);

  // Fetch employee data for roleId === 13
  useEffect(() => {
    if (roleId === '13' && employeeId) {
      const fetchEmployeeData = async () => {
        try {
          //const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
          const response = await getEmployeResignationByEmpId(employeeId)
          const employee = response.data;

          // Assuming the API returns data in the format { employeeId, employeeName, contactNumber, designation }
          setFormData((prev) => ({
            ...prev,
            empId: employee.employeeId || '',
            empName: employee.employeeName || '',
            contactNumber: employee.contactNumber || '',
            designation: employee.designation || '',
            client: employee.client || '',
            project: employee.project || '',
          }));
          setSelectedEmployee(employee.employeeId); // Set selected employee to disable dropdown
        } catch (error) {
          console.error('Error fetching employee data:', error);
          setErrors((prev) => ({ ...prev, general: 'Failed to load employee data' }));
        }
      };
      fetchEmployeeData();
    }
  }, [roleId, employeeId]);

  // Update form data when an employee is selected (only for roleId === 1)
  useEffect(() => {
    if (roleId === '1' || roleId === '15' && selectedEmployee) {
      const employee = employeeOptions.find((opt) => opt.value === selectedEmployee)?.employeeData;
      if (employee) {
        setFormData((prev) => ({
          ...prev,
          empId: employee.employeeId || '',
          empName: employee.employeeName || '',
          contactNumber: employee.contactNumber || '',
          designation: employee.designation || '',
          client: employee.client || '',
          project: employee.project || '',
        }));
      }
    } else if (roleId === '1' || roleId === '15' && !selectedEmployee) {
      setFormData((prev) => ({
        ...prev,
        empId: '',
        empName: '',
        contactNumber: '',
        designation: '',
        client: '',
        project: '',
      }));
    }
  }, [selectedEmployee, employeeOptions, roleId]);

  const handleInputChange = (selectedOption, field) => {
    if (roleId === '1' || roleId === '15') {
      const value = selectedOption ? selectedOption.value : null;
      switch (field) {
        case 'state':
          setState(value);
          setDistrict(null);
          setSelectedEmployee(null);
          setEmployeeOptions([]);
          break;
        case 'district':
          setDistrict(value);
          setSelectedEmployee(null);
          setEmployeeOptions([]);
          break;
        case 'employee':
          setSelectedEmployee(value);
          break;
        default:
          break;
      }
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };
  const resetForm = () => {
    setFormData({
      empId: '',
      empName: '',
      contactNumber: '',
      designation: '',
      client: '',
      project: '',
      reason: '',
      date: '',
      releavingDate: '',
    });
    setFile(null);
    if (roleId === '1' || roleId === '15') {
      setSelectedEmployee(null);
      setState(null);
      setDistrict(null);
    }
    // Reset file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formDataToSend = new FormData();

    // Append resignation data
    formDataToSend.append('resignationData', JSON.stringify({
      employeeId: formData.empId,
      reason: formData.reason,
      lastDate: formData.date,
      noticePeriodDate: formData.releavingDate,
      feedback: formData.feedback || '', // optional
      auditLog: getAuditLog(),
    }));

    // Append file
    if (file) {
      formDataToSend.append('file', file);
    }

    try {
      //const baseUrl = `http://localhost:8080/api/resignations/4G-20251105778443`;

      if (isUpdating && existingData?.rid) {
        const response = await axios.put(`http://localhost:8080/api/resign/${existingData.rid}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        alert('Resignation updated successfully.');
        setIsUpdating(false);
        setExistingData(response.data);
      } else {
        // await axios.post(`http://localhost:8080/api/resignations/${formData.empId}`, formDataToSend, {
        //   headers: { 'Content-Type': 'multipart/form-data' },
        // });

        await sendResignationMail(formData.empId, formDataToSend);
        alert('Resignation submitted successfully.');
      }

      // Reset form
      resetForm();
    } catch (error) {
      console.error('Submission failed:', error.response?.data || error.message);
      alert('Failed to submit. Please try again.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  return (
    <div className="resignList-container">
      <div className="resignList-box">
        <h2 className="resignList-heading">Resignation Form</h2>
        {errors.general && <div className="error-message">{errors.general}</div>}
        <form onSubmit={handleSubmit}>
          <div className="resignList-maincontainer">
            {roleId === '1' || roleId === '15' && (
              <>
                <div className="resignList-container-part">
                  <div
                    className={`resignList-floating-label-container ${isFocused.state || state ? 'float-label' : ''
                      }`}
                  >
                    <Select
                      name="state"
                      options={stateOptions}
                      value={stateOptions.find((opt) => opt.value === state) || null}
                      onChange={(option) => handleInputChange(option, 'state')}
                      onFocus={() => setIsFocused((prev) => ({ ...prev, state: true }))}
                      onBlur={() => setIsFocused((prev) => ({ ...prev, state: false }))}
                      placeholder=" "
                      isClearable
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                    <label>
                      <span className="required">*</span>State
                    </label>
                    {errors.state && <div className="error-message">{errors.state}</div>}
                  </div>
                </div>
                <div className="resignList-container-part">
                  <div
                    className={`resignList-floating-label-container ${isFocused.district || district ? 'float-label' : ''
                      }`}
                  >
                    <Select
                      name="district"
                      options={districtOptions}
                      value={districtOptions.find((opt) => opt.value === district) || null}
                      onChange={(option) => handleInputChange(option, 'district')}
                      onFocus={() => setIsFocused((prev) => ({ ...prev, district: true }))}
                      onBlur={() => setIsFocused((prev) => ({ ...prev, district: false }))}
                      placeholder=" "
                      isClearable
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                    <label>District</label>
                    {errors.district && <div className="error-message">{errors.district}</div>}
                  </div>
                </div>
                <div className="resignList-container-part">
                  <div
                    className={`resignList-floating-label-container ${isFocused.employee || selectedEmployee ? 'float-label' : ''
                      }`}
                  >
                    <Select
                      name="employee"
                      options={employeeOptions}
                      value={employeeOptions.find((opt) => opt.value === selectedEmployee) || null}
                      onChange={(option) => handleInputChange(option, 'employee')}
                      onFocus={() => setIsFocused((prev) => ({ ...prev, employee: true }))}
                      onBlur={() => setIsFocused((prev) => ({ ...prev, employee: false }))}
                      placeholder=" "
                      isClearable
                      isDisabled={!state} // Disable until state is selected
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                    <label>
                      <span className="required">*</span>Employee
                    </label>
                    {errors.employee && <div className="error-message">{errors.employee}</div>}
                  </div>
                </div>
              </>
            )}
          </div>
          {roleId !== '1' || roleId !== '15' && (
            <>
              <div className="resignList-label">
                <input
                  type="text"
                  name="empName"
                  value={formData.empName}
                  onChange={handleChange}
                  placeholder=""
                  readOnly
                />
                <label>Employee Name</label>
              </div>
              <div className="resignList-label">
                <input
                  type="text"
                  name="empId"
                  value={formData.empId}
                  onChange={handleChange}
                  placeholder=""
                  readOnly
                />
                <label>Employee ID</label>
              </div>
            </>
          )}
          <div className="resignList-label">
            <input
              type="text"
              name="contactNumber"
              value={formData.contactNumber}
              onChange={handleChange}
              placeholder=""
              readOnly
            />
            <label>Contact Number</label>
          </div>
          <div className="resignList-label">
            <input
              type="text"
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              placeholder=""
              readOnly
            />
            <label>Designation</label>
          </div>

          <div className="resignList-label">
            <input
              type="text"
              name="client"
              value={formData.client}
              onChange={handleChange}
              placeholder=""
              readOnly
            />
            <label>Client</label>
          </div>

          <div className="resignList-label">
            <input
              type="text"
              name="project"
              value={formData.project}
              onChange={handleChange}
              placeholder=""
              readOnly
            />
            <label>Project</label>
          </div>

          <div className="resignList-label">
            <input
              type="text"
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              required
              placeholder=""
            />
            <label>Reason for Resignation</label>
          </div>
          <div className={`resignList-label ${formData.date ? 'filled' : ''}`}>
            <DatePicker
              selected={formData.date ? new Date(formData.date) : null}
              onChange={(date) =>
                handleChange({
                  target: {
                    name: 'date',
                    value: date ? date.toLocaleDateString('en-CA') : '',
                  },
                })
              }
              minDate={new Date()}
              dateFormat="dd-MM-yyyy"
              placeholderText=""
            />
            <label>Resignation Date</label>
            {formData.date && (
              <div className="notice-message" style={{ marginTop: '5px', color: '#cf1515ff', fontSize: '14px' }}>
                Default Notice Period: 60 Days
              </div>
            )}
          </div>
          <div className={`resignList-label ${formData.releavingDate ? 'filled' : ''}`}>
            <DatePicker
              selected={formData.releavingDate ? new Date(formData.releavingDate) : null}
              onChange={(releavingDate) =>
                handleChange({
                  target: {
                    name: 'releavingDate',
                    value: releavingDate ? releavingDate.toLocaleDateString('en-CA') : '',
                  },
                })
              }
              minDate={new Date()}
              dateFormat="dd-MM-yyyy"
              placeholderText=""
            />
            <label>Relieving Request Date</label>
          </div>
          {roleId !== '1' || roleId !== '15'&& (
            <div className="resignList-label">
              <input
                type="file"
                name="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                required
              />
              <label>Upload Resignation Letter (PDF/DOC/JPG/PNG)</label>
            </div>
          )}
          <div className="resignList-btn-div">
            <button
              type="submit"
              className="resignList-btn"
              disabled={!isFetchDataEnabled}
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResignList;